import { useId, useRef, type KeyboardEvent } from 'react';

interface LwSegmentedBaseItem<T extends string> {
  value: T;
  label: string;
  count?: number;
  disabled?: boolean;
}

export interface LwSegmentedTabItem<T extends string> extends LwSegmentedBaseItem<T> {
  panelId: string;
  tabId?: string;
}

export interface LwSegmentedChoiceItem<T extends string> extends LwSegmentedBaseItem<T> {
  panelId?: never;
  tabId?: never;
}

export type LwSegmentedItem<T extends string> = LwSegmentedTabItem<T> | LwSegmentedChoiceItem<T>;

interface LwSegmentedBaseProps<T extends string> {
  value: T;
  onValueChange: (value: T) => void;
  ariaLabel: string;
  className?: string;
}

export type LwSegmentedProps<T extends string> = LwSegmentedBaseProps<T> & (
  | {
    mode?: 'tabs';
    items: readonly LwSegmentedTabItem<T>[];
  }
  | {
    mode: 'single-select';
    items: readonly LwSegmentedChoiceItem<T>[];
  }
);

/** Controlled tabs or single-select choices with roving keyboard focus. */
export function LwSegmented<T extends string>({
  value,
  items,
  onValueChange,
  ariaLabel,
  className,
  mode = 'tabs'
}: LwSegmentedProps<T>) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);
  const generatedId = useId();
  const activeValue = items.some((item) => item.value === value && !item.disabled)
    ? value
    : items.find((item) => !item.disabled)?.value;

  const move = (event: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
    const enabled = items.map((item, index) => ({ item, index })).filter(({ item }) => !item.disabled);
    if (enabled.length === 0) {
      return;
    }

    const currentEnabledIndex = enabled.findIndex(({ index }) => index === currentIndex);
    let target = currentEnabledIndex;
    if (event.key === 'ArrowRight' || (mode === 'single-select' && event.key === 'ArrowDown')) {
      target = (currentEnabledIndex + 1) % enabled.length;
    } else if (event.key === 'ArrowLeft' || (mode === 'single-select' && event.key === 'ArrowUp')) {
      target = (currentEnabledIndex - 1 + enabled.length) % enabled.length;
    } else if (event.key === 'Home') {
      target = 0;
    } else if (event.key === 'End') {
      target = enabled.length - 1;
    } else {
      return;
    }

    event.preventDefault();
    const next = enabled[target];
    refs.current[next.index]?.focus();
    onValueChange(next.item.value);
  };

  return (
    <div
      className={`lw-segmented${className ? ` ${className}` : ''}`}
      role={mode === 'tabs' ? 'tablist' : 'radiogroup'}
      aria-label={ariaLabel}
      aria-orientation="horizontal"
    >
      {items.map((item, index) => (
        <button
          key={item.value}
          ref={(node) => {
            refs.current[index] = node;
          }}
          type="button"
          role={mode === 'tabs' ? 'tab' : 'radio'}
          id={item.tabId ?? `${generatedId}-${index}`}
          aria-selected={mode === 'tabs' ? item.value === activeValue : undefined}
          aria-checked={mode === 'single-select' ? item.value === activeValue : undefined}
          aria-controls={mode === 'tabs' ? item.panelId : undefined}
          tabIndex={item.value === activeValue ? 0 : -1}
          disabled={item.disabled}
          onClick={() => onValueChange(item.value)}
          onKeyDown={(event) => move(event, index)}
        >
          {item.label}
          {item.count !== undefined && <span>{item.count}</span>}
        </button>
      ))}
    </div>
  );
}
